import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import invariant from 'tiny-invariant'

import {
  CLIENT_PARTY_TYPE,
  REFERRAL_TYPE_OPTION,
  SERVICE_AREA_ID,
} from '@/lib/constants'
import {
  getNextStep,
  getPreviousStep,
  toSafeHttpUrl,
} from '@/lib/helpers/helperFunctions'
import { getQueryParam } from '@/lib/helpers/routeHelpers'
import { SEARCH_PARAMS } from '@/lib/searchParams'
import clientService from '@/lib/services/clientService'
import referralFlowService from '@/lib/services/referralFlowService'
import webReferralService from '@/lib/services/webReferralService'
import { REFERRAL_DETAILS_TAB } from '@/pages/referClient/constants'
import { ClientDetailsFormValues } from '@/pages/referClient/steps/1_ClientDetails/types'
import {
  AdditionalReferralDetailsFormValues,
  ReferralDetailsTab,
} from '@/pages/referClient/types'
import {
  getServiceAreasByIds,
  selectServiceAreas,
} from '@/redux/slices/commonData'
import { selectCurrentCompanyOrFail } from '@/redux/slices/session'
import { RootState } from '@/redux/store'
import {
  ReferralIndividual,
  ReferralTypeOption,
  SerializableFile,
} from '@/types/misc'
import { ClientItem } from '@/types/responses/clients'
import { CompanyDetailsItem } from '@/types/responses/companies'
import { CompassReportCollectionItem } from '@/types/responses/compass-reports'
import { PanelReferralApprovalsUser } from '@/types/responses/panels'
import { ExistingAdviserReferralMetadataItem } from '@/types/responses/referral-metadata'

export interface WebReferralState {
  // Shared state
  currentStep: WebReferralStep
  existingAdviserReferralMetadata?: ExistingAdviserReferralMetadataItem
  newRelationshipApprovers?: PanelReferralApprovalsUser[]
  isShowingRequestApprovalModal?: boolean
  approvalRequestMessage?: string

  clientDetails: {
    form: ClientDetailsFormValues
    persistedClient?: ClientItem
    isShowingAdditionalClientDetails: boolean
    compassReport?: CompassReportCollectionItem
    attachments: SerializableFile[]
  }

  referralDetails: {
    referralMethod?: ReferralMethod
    tab: ReferralDetailsTab
    adviserEmail?: string
    serviceAreaIds: number[]
    // The location the firm search was narrowed to, e.g. "country:NP"
    firmSearchLocation?: string
    referralFirm?: CompanyDetailsItem
    referralFirmIndividuals: ReferralIndividual[]
    additionalReferralDetails?: AdditionalReferralDetailsFormValues
  }

  confirmReferral: {
    userEmailMessage?: string
    referralType?: ReferralTypeOption
    requestCallbackForm?: {
      callbackTimes?: string[]
      callbackTelephone?: string
    }
    commissionJustification?: string
    hasEditedCommissionJustification?: boolean
  }
}

export type ReferralMethod = 'existingAdviser' | 'newAdviser'

export const REFERRAL_METHOD: Record<ReferralMethod, ReferralMethod> = {
  existingAdviser: 'existingAdviser',
  newAdviser: 'newAdviser',
}

export type WebReferralStep =
  | 'clientDetails'
  | 'referralDetails'
  | 'commercialAgreement'
  | 'confirmReferralToExistingAdviser'
  | 'confirmReferralToNewAdviser'
  | 'confirmPcoReferralToExistingAdviser'

export const WEB_REFERRAL_STEP: Record<WebReferralStep, WebReferralStep> = {
  clientDetails: 'clientDetails',
  referralDetails: 'referralDetails',
  commercialAgreement: 'commercialAgreement',
  confirmReferralToExistingAdviser: 'confirmReferralToExistingAdviser',
  confirmPcoReferralToExistingAdviser: 'confirmPcoReferralToExistingAdviser',
  confirmReferralToNewAdviser: 'confirmReferralToNewAdviser',
}

export const INITIAL_STATE: WebReferralState = {
  currentStep: WEB_REFERRAL_STEP.clientDetails,
  confirmReferral: {},
  clientDetails: {
    form: webReferralService.buildInitialClientDetailsValues(),
    isShowingAdditionalClientDetails: false,
    attachments: [],
  },
  referralDetails: {
    tab: REFERRAL_DETAILS_TAB.selectFirm,
    serviceAreaIds: [],
    referralFirmIndividuals: [],
  },
}

const sliceName = 'webReferral'

export function buildInitialWebReferralState(): WebReferralState {
  return {
    ...INITIAL_STATE,
    referralDetails: {
      ...INITIAL_STATE.referralDetails,
      referralMethod: getQueryParam<ReferralMethod | undefined>({
        name: SEARCH_PARAMS.referClient.referralMethod,
        transform: (value) =>
          webReferralService.isValidReferralTarget(value) ? value : undefined,
      }),
    },
  }
}

const webReferral = createSlice({
  name: sliceName,
  initialState: buildInitialWebReferralState,
  reducers: {
    initialiseReferClient() {
      return buildInitialWebReferralState()
    },

    goToStep(state, action: PayloadAction<WebReferralStep>) {
      state.currentStep = action.payload
    },

    setReferralDetailsTab(
      state,
      action: PayloadAction<WebReferralState['referralDetails']['tab']>,
    ) {
      state.referralDetails.tab = action.payload
    },

    setReferralMethod(
      state,
      action: PayloadAction<
        WebReferralState['referralDetails']['referralMethod']
      >,
    ) {
      state.referralDetails.referralMethod = action.payload
    },

    setExistingAdviserReferralMetadata: (
      state,
      action: PayloadAction<
        WebReferralState['existingAdviserReferralMetadata']
      >,
    ) => {
      state.existingAdviserReferralMetadata = action.payload
    },

    setReferralFirm: (
      state,
      action: PayloadAction<
        WebReferralState['referralDetails']['referralFirm']
      >,
    ) => {
      const previousReferralFirmId = state.referralDetails.referralFirm?.id
      const newReferralFirmId = action.payload?.id

      if (action.payload?.isAffiliateLinkBased) {
        state.confirmReferral.referralType = REFERRAL_TYPE_OPTION.affiliateLink
      }

      const hasClearedFirm = !newReferralFirmId
      const hasChangedFirm =
        !!newReferralFirmId && newReferralFirmId !== previousReferralFirmId

      // Clear any state that will become invalid when the referral firm changes
      if (hasClearedFirm || hasChangedFirm) {
        state.existingAdviserReferralMetadata =
          INITIAL_STATE.existingAdviserReferralMetadata
        state.referralDetails.referralFirmIndividuals =
          INITIAL_STATE.referralDetails.referralFirmIndividuals
        state.confirmReferral.referralType =
          INITIAL_STATE.confirmReferral.referralType
      }

      state.referralDetails.referralFirm = action.payload
    },

    setReferralFirmIndividuals: (
      state,
      action: PayloadAction<
        WebReferralState['referralDetails']['referralFirmIndividuals']
      >,
    ) => {
      state.referralDetails.referralFirmIndividuals = action.payload
    },

    setAdditionalReferralDetails: (
      state,
      action: PayloadAction<
        WebReferralState['referralDetails']['additionalReferralDetails']
      >,
    ) => {
      state.referralDetails.additionalReferralDetails = action.payload
    },

    setFirmSearchLocation: (
      state,
      action: PayloadAction<
        WebReferralState['referralDetails']['firmSearchLocation']
      >,
    ) => {
      state.referralDetails.firmSearchLocation = action.payload
    },

    setServiceAreaIds: (
      state,
      action: PayloadAction<
        WebReferralState['referralDetails']['serviceAreaIds']
      >,
    ) => {
      state.referralDetails.serviceAreaIds = action.payload
    },

    setClientDetailsFormValues: (
      state,
      action: PayloadAction<WebReferralState['clientDetails']['form']>,
    ) => {
      state.clientDetails = {
        ...state.clientDetails,
        form: action.payload,
      }
    },

    setClientCircumstances: (state, action: PayloadAction<string>) => {
      state.clientDetails.form.circumstances = action.payload
    },

    setPersistedClient: (state, action: PayloadAction<ClientItem>) => {
      const previousClient = state.clientDetails.persistedClient
      const selectedClient = action.payload

      state.clientDetails.persistedClient = selectedClient

      state.clientDetails.form = {
        ...state.clientDetails.form,
        partyType: selectedClient.partyType,
        firstName: selectedClient.firstName,
        lastName: selectedClient.lastName,
        email: selectedClient.email,
        telephone: selectedClient.telephone || '',
        dateOfBirth: selectedClient.dateOfBirth || '',
        clientId: selectedClient.clientId || '',
        circumstances: state.clientDetails.form.circumstances || '',
        addressLine1: selectedClient.addressLine1 || '',
        city: selectedClient.city || '',
        postcode: selectedClient.postcode || '',
      }

      // Clear Compass report if the client has changed
      if (previousClient && selectedClient.id !== previousClient.id) {
        state.clientDetails.compassReport = undefined
      }
    },

    clearPersistedClient: (state) => {
      state.clientDetails.persistedClient = undefined
      state.clientDetails.compassReport =
        INITIAL_STATE.clientDetails.compassReport
      state.clientDetails = INITIAL_STATE.clientDetails
    },

    setCompassReport: (
      state,
      action: PayloadAction<WebReferralState['clientDetails']['compassReport']>,
    ) => {
      state.clientDetails.compassReport = action.payload
    },

    clearCompassReport: (state) => {
      state.clientDetails.compassReport = undefined
    },

    setReferralType: (
      state,
      action: PayloadAction<
        WebReferralState['confirmReferral']['referralType']
      >,
    ) => {
      state.confirmReferral.referralType = action.payload
    },

    clearReferralType(state) {
      state.confirmReferral.referralType =
        INITIAL_STATE.confirmReferral.referralType
    },

    setUserEmailMessage: (
      state,
      action: PayloadAction<
        WebReferralState['confirmReferral']['userEmailMessage']
      >,
    ) => {
      state.confirmReferral.userEmailMessage = action.payload
    },

    setApprovalRequestMessage: (state, action: PayloadAction<string>) => {
      state.approvalRequestMessage = action.payload
    },

    setAttachments: (
      state,
      action: PayloadAction<WebReferralState['clientDetails']['attachments']>,
    ) => {
      state.clientDetails.attachments = action.payload
    },

    setRequestCallbackForm: (
      state,
      action: PayloadAction<
        WebReferralState['confirmReferral']['requestCallbackForm']
      >,
    ) => {
      state.confirmReferral.requestCallbackForm = action.payload
    },

    setCommissionJustification: (
      state,
      action: PayloadAction<
        WebReferralState['confirmReferral']['commissionJustification']
      >,
    ) => {
      state.confirmReferral.commissionJustification = action.payload
    },

    setHasEditedCommissionJustification: (
      state,
      action: PayloadAction<
        WebReferralState['confirmReferral']['hasEditedCommissionJustification']
      >,
    ) => {
      state.confirmReferral.hasEditedCommissionJustification = action.payload
    },

    setNewRelationshipApprovers: (
      state,
      action: PayloadAction<WebReferralState['newRelationshipApprovers']>,
    ) => {
      state.newRelationshipApprovers = action.payload
    },

    showRequestApprovalModal: (state) => {
      state.isShowingRequestApprovalModal = true
    },

    hideRequestApprovalModal: (state) => {
      state.isShowingRequestApprovalModal = false
    },

    showAdditionalClientDetails: (state) => {
      state.clientDetails.isShowingAdditionalClientDetails = true
    },

    hideAdditionalClientDetails: (state) => {
      state.clientDetails.isShowingAdditionalClientDetails = false
    },

    setAdviserEmail(
      state,
      action: PayloadAction<
        WebReferralState['referralDetails']['adviserEmail']
      >,
    ) {
      state.referralDetails.adviserEmail = action.payload
    },

    reset(_state) {
      return INITIAL_STATE
    },

    resetClientDetails(
      state,
      action: PayloadAction<
        Partial<WebReferralState['clientDetails']> | undefined
      >,
    ) {
      state.clientDetails = {
        ...INITIAL_STATE.clientDetails,
        ...action.payload,
      }
    },

    resetReferralDetails(
      state,
      action: PayloadAction<
        Partial<WebReferralState['referralDetails']> | undefined
      >,
    ) {
      state.referralDetails = {
        ...INITIAL_STATE.referralDetails,
        tab: state.referralDetails.tab, // Don't reset the tab
        ...action.payload,
      }
    },

    resetConfirmReferralDetails(
      state,
      action: PayloadAction<
        Partial<WebReferralState['confirmReferral']> | undefined
      >,
    ) {
      state.confirmReferral = {
        ...INITIAL_STATE.confirmReferral,
        ...action.payload,
      }
    },
  },
})

export const webReferralReducer = webReferral.reducer

export const {
  initialiseReferClient,
  goToStep,
  setReferralMethod,
  setExistingAdviserReferralMetadata,
  setReferralFirm,
  setReferralFirmIndividuals,
  setAdditionalReferralDetails,
  setFirmSearchLocation,
  setServiceAreaIds,
  setPersistedClient,
  clearPersistedClient,
  setClientCircumstances,
  setCompassReport,
  clearCompassReport,
  setReferralType,
  clearReferralType,
  setUserEmailMessage,
  setAttachments,
  setClientDetailsFormValues,
  setRequestCallbackForm,
  setApprovalRequestMessage,
  setNewRelationshipApprovers,
  showRequestApprovalModal,
  hideRequestApprovalModal,
  showAdditionalClientDetails,
  hideAdditionalClientDetails,
  setAdviserEmail,
  setCommissionJustification,
  setHasEditedCommissionJustification,
  reset,
  resetClientDetails,
  resetReferralDetails,
  resetConfirmReferralDetails,
} = webReferral.actions

export const removeReferralFirmSelection = createAsyncThunk(
  `${sliceName}/removeReferralFirmSelection`,
  async (_, { dispatch }) => {
    dispatch(setReferralFirm(undefined))

    const url = new URL(window.location.href)
    const params = url.searchParams
    params.delete(SEARCH_PARAMS.referClient.referralFirmId)
    window.history.pushState({}, '', url)
  },
)

const NO_APPROVED_PANEL_STEP_NAMES: WebReferralStep[] = [
  WEB_REFERRAL_STEP.clientDetails,
  WEB_REFERRAL_STEP.referralDetails,
  WEB_REFERRAL_STEP.confirmReferralToNewAdviser,
]

const ADVISER_EMAIL_REFER_CLIENT_STEP_NAMES: WebReferralStep[] = [
  WEB_REFERRAL_STEP.clientDetails,
  WEB_REFERRAL_STEP.referralDetails,
  WEB_REFERRAL_STEP.confirmReferralToNewAdviser,
]

const PCO_REFER_CLIENT_STEP_NAMES: WebReferralStep[] = [
  WEB_REFERRAL_STEP.clientDetails,
  WEB_REFERRAL_STEP.referralDetails,
  WEB_REFERRAL_STEP.confirmPcoReferralToExistingAdviser,
]

const NORMAL_REFER_CLIENT_STEP_NAMES: WebReferralStep[] = [
  WEB_REFERRAL_STEP.clientDetails,
  WEB_REFERRAL_STEP.referralDetails,
  WEB_REFERRAL_STEP.commercialAgreement,
  WEB_REFERRAL_STEP.confirmReferralToExistingAdviser,
]

const INTERNAL_REFER_CLIENT_STEP_NAMES: WebReferralStep[] = [
  WEB_REFERRAL_STEP.clientDetails,
  WEB_REFERRAL_STEP.referralDetails,
  WEB_REFERRAL_STEP.confirmReferralToExistingAdviser,
]

export type ReferClientSteps = Array<keyof typeof WEB_REFERRAL_STEP>

export const selectWebReferral = (state: RootState) => state.webReferral

export const selectClientType = createSelector(
  selectWebReferral,
  (state) => state.clientDetails?.form?.partyType || CLIENT_PARTY_TYPE.PERSON,
)

export const selectReferralDetails = createSelector(
  selectWebReferral,
  (state) => state.referralDetails,
)

export const selectReferralMethod = createSelector(
  selectReferralDetails,
  (referralDetails) => referralDetails.referralMethod,
)

export const selectReferralFirm = createSelector(
  selectReferralDetails,
  (referralDetails) => referralDetails.referralFirm,
)

export const selectFirmSearchLocation = createSelector(
  selectReferralDetails,
  (referralDetails) => referralDetails.firmSearchLocation,
)

export const selectReferralFirmOrFail = createSelector(
  selectReferralFirm,
  (referralFirm) => {
    invariant(referralFirm, 'Expected referralFirm to be set')

    return referralFirm
  },
)

export const selectReferralDetailsTab = createSelector(
  selectReferralDetails,
  (referralDetails) => referralDetails.tab,
)

export const selectCurrentStep = createSelector(
  selectWebReferral,
  (state) => state.currentStep,
)

export const selectServiceAreaIds = createSelector(
  selectReferralDetails,
  (referralDetails) => referralDetails.serviceAreaIds,
)

export const selectIsPcoReferral = createSelector(
  selectServiceAreaIds,
  selectReferralFirm,
  (serviceAreaIds, referralFirm): boolean => {
    return (
      serviceAreaIds.includes(SERVICE_AREA_ID.will) && !!referralFirm?.isPco
    )
  },
)

export const selectIsReferringToAdviserEmail = createSelector(
  selectReferralMethod,
  (referralMethod): boolean => {
    return referralMethod === REFERRAL_METHOD.newAdviser
  },
)

export const selectIsReferringToExistingAdviser = createSelector(
  selectIsReferringToAdviserEmail,
  (isReferringToAdviserEmail): boolean => {
    return !isReferringToAdviserEmail
  },
)

export const selectReferralFirmIndividuals = createSelector(
  selectReferralDetails,
  (state) => state.referralFirmIndividuals,
)

export const selectExistingAdviserReferralMetadata = createSelector(
  selectWebReferral,
  (state) => state.existingAdviserReferralMetadata,
)

export const selectExistingAdviserReferralMetadataOrFail = createSelector(
  selectWebReferral,
  (state) => {
    invariant(
      state.existingAdviserReferralMetadata,
      'Expected existingAdviserReferralMetadata to be set',
    )

    return state.existingAdviserReferralMetadata
  },
)

export const selectConsentType = createSelector(
  selectExistingAdviserReferralMetadata,
  (metadata) => referralFlowService.getConsentType(metadata),
)

export const selectIsInternalReferral = createSelector(
  selectExistingAdviserReferralMetadata,
  (metadata) => !!metadata?.panel?.internalRelationship,
)

export const selectNewRelationshipApprovers = createSelector(
  selectWebReferral,
  (state) => state.newRelationshipApprovers,
)

export const selectSteps = createSelector(
  selectCurrentCompanyOrFail,
  selectIsReferringToAdviserEmail,
  selectIsPcoReferral,
  selectIsInternalReferral,
  (
    currentCompany,
    isReferringToAdviserEmail,
    isPcoReferral,
    isInternalReferral,
  ) => {
    function getSteps() {
      if (!currentCompany.hasApprovedPanel) {
        return NO_APPROVED_PANEL_STEP_NAMES
      }

      if (isReferringToAdviserEmail) {
        return ADVISER_EMAIL_REFER_CLIENT_STEP_NAMES
      }

      if (isPcoReferral) {
        return PCO_REFER_CLIENT_STEP_NAMES
      }

      if (isInternalReferral) {
        return INTERNAL_REFER_CLIENT_STEP_NAMES
      }

      return NORMAL_REFER_CLIENT_STEP_NAMES
    }

    return getSteps()
  },
)

export const selectNextStep = createSelector(
  selectSteps,
  selectCurrentStep,
  (steps, currentStep) => {
    return getNextStep<ReferClientSteps>(steps, currentStep)
  },
)

export const selectPreviousStep = createSelector(
  selectSteps,
  selectCurrentStep,
  (steps, currentStep) => {
    return getPreviousStep<ReferClientSteps>(steps, currentStep)
  },
)

export const selectAdditionalReferralDetails = createSelector(
  selectReferralDetails,
  (state) => state.additionalReferralDetails,
)

export const selectSelectedServiceAreas = createSelector(
  selectServiceAreas(),
  selectServiceAreaIds,
  (serviceAreas, serviceAreaIds) => {
    return getServiceAreasByIds(serviceAreas, serviceAreaIds)
  },
)

export const selectPersistedClient = createSelector(
  selectWebReferral,
  (state) => state.clientDetails.persistedClient,
)

export const selectPersistedClientOrFail = createSelector(
  selectPersistedClient,
  (client) => {
    invariant(client, 'Expected client to be set')

    return client
  },
)

export const selectClientDetails = createSelector(
  selectWebReferral,
  (state) => state.clientDetails,
)

export const selectClientDetailsForm = createSelector(
  selectWebReferral,
  (state) => state.clientDetails.form,
)

export const selectCompassReport = createSelector(
  selectClientDetails,
  (clientDetails) => clientDetails.compassReport,
)

export const selectConfirmReferral = createSelector(
  selectWebReferral,
  (webReferral) => webReferral.confirmReferral,
)

export const selectReferralType = createSelector(
  selectConfirmReferral,
  (state) => state.referralType,
)

export const selectIsNormalReferralType = createSelector(
  selectReferralType,
  (referralTypeOption) =>
    referralFlowService.isNormalReferralType(referralTypeOption),
)

export const selectIsBookingCallForClient = createSelector(
  selectReferralType,
  (referralTypeOption) =>
    webReferralService.isBookCallForClientReferralType(referralTypeOption),
)

/**
 * The company's booking link for client calls, or null if it hasn't one or it
 * isn't http(s). Everything in the "book a call for the client" flow (UI,
 * validation and the request) goes by this so they agree on whether there's a
 * link to book through.
 */
export const selectSafeScheduleClientCallUrl = createSelector(
  selectExistingAdviserReferralMetadata,
  (metadata) => toSafeHttpUrl(metadata?.toCompany.scheduleClientCallUrl),
)

export const selectIsRequestingReferralCallbackForClient = createSelector(
  selectIsBookingCallForClient,
  selectSafeScheduleClientCallUrl,
  (isBookingCallForClient, scheduleClientCallUrl) => {
    return isBookingCallForClient && !scheduleClientCallUrl
  },
)

export const selectIsSchedulingMeetingForClient = createSelector(
  selectIsBookingCallForClient,
  selectSafeScheduleClientCallUrl,
  (isBookingCallForClient, scheduleClientCallUrl) => {
    return isBookingCallForClient && !!scheduleClientCallUrl
  },
)

export const selectUserEmailMessage = createSelector(
  selectConfirmReferral,
  (state) => state.userEmailMessage,
)

export const selectNormalizedReferralType = createSelector(
  selectReferralType,
  (referralType) => referralFlowService.normalizeReferralType(referralType),
)

export const selectAttachments = createSelector(
  selectClientDetails,
  (clientDetails) => clientDetails.attachments,
)

export const selectRequestCallbackForm = createSelector(
  selectConfirmReferral,
  (state) => state.requestCallbackForm,
)

export const selectCommissionJustification = createSelector(
  selectConfirmReferral,
  (state) => state.commissionJustification,
)

export const selectHasEditedCommissionJustification = createSelector(
  selectConfirmReferral,
  (state) => !!state.hasEditedCommissionJustification,
)

export const selectApprovalRequestMessage = createSelector(
  selectWebReferral,
  (webReferral) => webReferral.approvalRequestMessage,
)

export const selectIsShowingRequestApprovalModal = createSelector(
  selectWebReferral,
  (webReferral) => webReferral.isShowingRequestApprovalModal,
)

export const selectIsShowingAdditionalClientDetails = createSelector(
  selectClientDetails,
  (clientDetails) => clientDetails.isShowingAdditionalClientDetails,
)

export const selectAdviserEmail = createSelector(
  selectReferralDetails,
  (referralDetails) => referralDetails.adviserEmail,
)

export const selectIsReferringAnIndividual = createSelector(
  selectClientType,
  (clientType) => clientType === CLIENT_PARTY_TYPE.PERSON,
)

export const selectIsReferringToBusinessOrTrust = createSelector(
  selectClientType,
  (clientType) => clientService.isBusinessOrTrust(clientType),
)

export const selectClientTypeLabel = createSelector(
  selectClientType,
  (clientType) => clientService.getClientTypeLabel(clientType),
)

export const selectRequiresColleagueApproval = createSelector(
  selectIsReferringToExistingAdviser,
  selectExistingAdviserReferralMetadata,
  selectNewRelationshipApprovers,
  (
    isReferringToExistingAdviser,
    existingAdviserReferralMetadata,
    newRelationshipApprovers = [],
  ) => {
    return isReferringToExistingAdviser
      ? !!existingAdviserReferralMetadata?.requiresColleagueApproval
      : newRelationshipApprovers.length > 0
  },
)

export const selectReferralJustification = createSelector(
  selectAdditionalReferralDetails,
  (additionalReferralDetails): string | undefined => {
    return additionalReferralDetails?.referralJustification
  },
)
